import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import AdminDashboard from './pages/AdminDashboard';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import ClientDashboard from './pages/ClientDashboard';
import ClientsPage from './pages/ClientsPage';
import AlertsPage from './pages/AlertsPage';
import MarketPage from './pages/MarketPage';
import UploadPage from './pages/UploadPage';
import CallbacksPage from './pages/CallbacksPage';
import CallbackRequestPage from './pages/CallbackRequestPage';
import SettingsPage from './pages/SettingsPage';
import RecommendationsPage from './pages/RecommendationsPage';
import PendingAlertsPage from './pages/PendingAlertsPage';
import FranchisesPage from './pages/FranchisesPage';
import TeamPage from './pages/TeamPage';
import PublicCallbackPage from './pages/PublicCallbackPage';
import ForceChangePassword from './pages/ForceChangePassword';
import AllClientsPage from './pages/AllClientsPage';

// Layout
import DashboardLayout from './components/DashboardLayout';

const getHome = (role) => {
  if (role === 'superadmin' || role === 'admin') return '/admin';
  return '/dashboard';
};

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F6F9] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#397DFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Force password change on first login
  if (user.must_change_password) {
    return <ForceChangePassword />;
  }

  if (requireAdmin && user.role !== 'admin' && user.role !== 'superadmin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (!requireAdmin && (user.role === 'admin' || user.role === 'superadmin')) {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F6F9] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#397DFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to={getHome(user.role)} replace />;
  }

  return children;
};

// Super admin gets its own dashboard, admins get theirs
const AdminIndex = () => {
  const { user } = useAuth();
  if (user?.role === 'superadmin') return <SuperAdminDashboard />;
  return <AdminDashboard />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/callback/:token" element={<PublicCallbackPage />} />

      {/* Admin / Super Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute requireAdmin><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<AdminIndex />} />
        <Route path="franchises" element={<FranchisesPage />} />
        <Route path="all-clients" element={<AllClientsPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="pending-alerts" element={<PendingAlertsPage />} />
        <Route path="market" element={<MarketPage />} />
        <Route path="recommendations" element={<RecommendationsPage />} />
        <Route path="upload" element={<UploadPage />} />
        <Route path="callbacks" element={<CallbacksPage />} />
        <Route path="team" element={<TeamPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Client Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<ClientDashboard />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="callback" element={<CallbackRequestPage />} />
      </Route>

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App">
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
